// ICE COLD — circle menu. Opening: the destinations spring out from the centre button,
// one after another, onto a circle. Closing: the circle spins a full turn while they fall
// back in (reverse stagger), the centre button swells and shakes, then the layer fades.
(() => {
  if (customElements.get('ice-circle-menu')) return;

  const OPEN_STAGGER = 20;
  const CLOSE_STAGGER = 70;
  const SPRING = 'cubic-bezier(0.34, 1.45, 0.64, 1)';

  class IceCircleMenu extends HTMLElement {
    connectedCallback() {
      this.opener = this.querySelector('[data-open]');
      this.layer = this.querySelector('.ice-circle-menu__layer');
      this.spin = this.querySelector('.ice-circle-menu__spin');
      this.trigger = this.querySelector('.ice-circle-menu__trigger');
      this.items = [...this.querySelectorAll('[data-item]')];
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isOpen = false;
      this.busy = false;

      // The layer must escape the header's stacking context to cover the page.
      document.body.append(this.layer);

      this.opener.addEventListener('click', (e) => (this.isOpen ? this.close() : this.open(e.detail === 0)));
      this.layer.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.layer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
    }

    place(open) {
      const n = this.items.length;
      const r = this.spin.offsetWidth / 2;
      this.items.forEach((item, i) => {
        const a = (2 * Math.PI * i) / n - Math.PI / 2;
        const x = open ? Math.cos(a) * r : 0;
        const y = open ? Math.sin(a) * r : 0;
        item.style.transform = `translate(${x}px, ${y}px) scale(${open ? 1 : 0.4})`;
        item.style.opacity = open ? '1' : '0';
      });
    }

    // fromKeyboard: move focus into the ring only for keyboard users (a tap would show focus rings).
    open(fromKeyboard = false) {
      if (this.busy) return;
      this.isOpen = true;
      this.opener.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ice-circle-open');
      this.layer.hidden = false;
      this.items.forEach((item, i) => {
        item.style.transition = 'none';
        item.style.transitionDelay = '0ms';
      });
      this.place(false);
      this.layer.offsetWidth;
      this.layer.classList.add('is-open');
      this.items.forEach((item, i) => {
        item.style.transition = '';
        item.style.transitionTimingFunction = SPRING;
        item.style.transitionDelay = `${this.reduced ? 0 : 60 + i * OPEN_STAGGER}ms`;
      });
      this.place(true);
      if (fromKeyboard) setTimeout(() => this.items[0].focus({ preventScroll: true }), 120);
    }

    async close() {
      if (!this.isOpen || this.busy) return;
      this.busy = true;
      const hadFocus = this.layer.contains(document.activeElement);
      this.isOpen = false;
      this.opener.setAttribute('aria-expanded', 'false');
      const n = this.items.length;

      if (!this.reduced) {
        this.items.forEach((item, i) => {
          item.style.transitionTimingFunction = 'cubic-bezier(0.5, 0, 0.75, 0)';
          item.style.transitionDelay = `${i * CLOSE_STAGGER}ms`;
        });
        this.place(false);

        const total = CLOSE_STAGGER * (n + 2);
        this.spin.animate(
          [{ transform: 'rotate(0)', filter: 'blur(0)' }, { transform: 'rotate(-360deg)', filter: 'blur(1px)' }],
          { duration: total, easing: 'linear' }
        );
        // Centre button swells step by step, fading toward the background, while it shakes.
        const steps = [];
        for (let i = 0; i < n; i++) {
          steps.push({ transform: `scale(${Math.min(1 + i * 0.15, 1.5)}) translateX(${i % 2 ? 2 : -2}px)`, backgroundColor: `color-mix(in srgb, #fff ${Math.max(100 - i * 10, 40)}%, #000)` });
        }
        steps.push({ transform: 'scale(1) translateX(0)', backgroundColor: '#fff' });
        this.trigger.animate(steps, { duration: total, easing: 'linear' });
        await new Promise((r) => setTimeout(r, total));
      } else {
        this.place(false);
      }

      this.layer.classList.remove('is-open');
      await new Promise((r) => setTimeout(r, this.reduced ? 0 : 220));
      this.layer.hidden = true;
      document.body.classList.remove('ice-circle-open');
      this.busy = false;
      if (hadFocus) this.opener.focus({ preventScroll: true });
    }

    trap(e) {
      const focusables = [...this.items, this.trigger];
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ice-circle-menu', IceCircleMenu);
})();
